// What counts as an unfilled placeholder, and where it is safe to look for one.
//
// An LLM handed a blank template writes scaffolding like `[Course Name]` or
// `[Your Institution]` into the prose fields, and the deck pipeline replaces
// those on the way in. The catch is that square brackets are not exclusively a
// placeholder: they are also CSS attribute selectors and named grid lines.
//
// A deck with a custom HTML/CSS block would come back from a reload with
// `[data-phase="paper"]` rewritten to `data-phase="paper"` — the token's inner
// text with its brackets eaten — which is not a selector any more. Every rule
// keyed on an attribute stopped matching and the slide lost its colour. Two
// guards, because either one alone still loses something real:
//
//   1. CODE_BEARING_KEYS  Fields that hold CSS or markup are never searched.
//      A bare `[data-phase]` or a grid line `[full-start]` is prose-shaped and
//      no pattern can tell it apart from a placeholder, so the answer is to not
//      ask the question about a stylesheet in the first place.
//
//   2. PLACEHOLDER_PATTERN  Prose-shaped only. A real placeholder is words in
//      brackets, so a token carrying `=`, quotes, or other selector punctuation
//      is not one — which keeps hand-written markup intact wherever it reaches
//      a field this does still scan.

/**
 * Deck fields whose value is code, not prose.
 *
 * `css` and `html` are the custom HTML/CSS block's two halves; `slideGlobalCss`
 * is the deck-wide stylesheet. Placeholder handling skips these keys entirely —
 * for detection and for replacement alike, so the validator cannot reject what
 * the repairer is no longer allowed to fix.
 */
export const CODE_BEARING_KEYS = new Set(['css', 'html', 'slideGlobalCss']);

export const isCodeBearingKey = (key) => CODE_BEARING_KEYS.has(key);

/**
 * A placeholder is words in brackets.
 *
 * Letters, digits, spaces and light punctuation, with at least one letter
 * required — so `[Course Name]` and `[Your Institution]` match, while `[1]`
 * (a citation marker in body text) and `[data-w="10"]` (an attribute selector
 * that slipped into a scanned field) do not.
 *
 * Callers get a fresh regex rather than a shared one: the `g` flag carries
 * `lastIndex` between calls, and a module-level constant shared by `test()` and
 * `replace()` would skip every other match.
 */
export const placeholderPattern = () => /\[[A-Za-z0-9 ._,&-]*[A-Za-z][A-Za-z0-9 ._,&-]*\]/g;
